import React, { useState } from 'react';
import api from '@/app/services/api.js';
import { PostActions } from '@/components/custom/post';
import moment from 'moment';
import 'moment/locale/pt';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { MagicLink } from '@/components/custom/magic-link';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AvatarWrapper } from '@/components/ui/avatar';

const PostCardWrapper = React.forwardRef(({ className, isClickable, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-card-wrapper',
      // Estilos Base
      'animate-fade-in cursor-auto',
      'rounded-[10px] border border-solid border-bright bg-white p-4 shadow-dropshadow transition-all duration-150',
      'flex flex-wrap items-start justify-between gap-5',
      // Estilos Responsivos
      'md:p-8',
      // Variantes
      isClickable && 'cursor-pointer hover:shadow-dropshadow-md',
      className
    )}
    {...props}
  />
));
PostCardWrapper.displayName = 'PostCardWrapper';

const PostCardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-card-content',
      // Estilos Base
      'flex w-10 grow flex-col',
      className
    )}
    {...props}
  />
));
PostCardContent.displayName = 'PostCardContent';

const PostCardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-card-header',
      // Estilos Base
      'mb-4',
      className
    )}
    {...props}
  />
));
PostCardHeader.displayName = 'PostCardHeader';

const PostCardName = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-post-card-name',
      // Estilo base
      'mb-0 cursor-pointer text-[0.9em] font-bold leading-[1.2em] text-primary-dark md:text-base md:leading-normal',
      className
    )}
    {...props}
  />
));
PostCardName.displayName = 'PostCardName';

const PostCardTime = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-post-card-time',
      // Estilo base
      'mb-0 text-[0.8em] text-neutral',
      className
    )}
    {...props}
  />
));
PostCardTime.displayName = 'PostCardTime';

const PostCardBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-post-card-body',
      // Estilos Base
      'whitespace-pre-line break-words text-[0.85em] leading-[1.5em]',
      // Estilos Responsivos
      'md:text-[0.92em]',
      className
    )}
    {...props}
  />
));
PostCardBody.displayName = 'PostCardBody';

const PostCardText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-post-card-text',
      // Estilos Base
      'mb-0 text-sm text-neutral-darker',
      className
    )}
    {...props}
  />
));
PostCardText.displayName = 'PostCardText';

const PostCard = React.forwardRef(({ className, ...props }, ref) => {
  const navigate = useNavigate();
  const [post, setPost] = useState(props.post);

  const deletePost = post => {
    api
      .delete(`posts/${post.id}`)
      .then(response => {
        setPost(null);
        toast('Mensagem deletada!', {
          icon: '🗑️'
        });
      })
      .catch(error => {
        console.error(error);
      });
  };

  const handleNavigateToPost = (event, route, options = {}) => {
    event.stopPropagation();
    navigate(route, options);
  };

  return (
    post && (
      <PostCardWrapper
        isClickable
        ref={ref}
        className={className}
        onClick={event => handleNavigateToPost(event, `/posts/${post.id}`)}
        {...props}
      >
        <AvatarWrapper
          size="sm"
          src={post?.user?.image?.url}
          text={post?.user?.name}
          onClick={event =>
            handleNavigateToPost(event, `/usuario/metricas/${props.post['user-id']}`, {
              state: { isRedirected: true }
            })
          }
        />

        <PostCardContent>
          <PostCardHeader>
            <PostCardName
              onClick={event =>
                handleNavigateToPost(event, `/usuario/metricas/${props.post['user-id']}`, {
                  state: { isRedirected: true }
                })
              }
            >
              {post?.user?.name}
            </PostCardName>
            <PostCardTime>{moment(post['created-at']).fromNow()}</PostCardTime>
          </PostCardHeader>

          <PostCardBody>
            <PostCardText>{ReactHtmlParser(post?.body)}</PostCardText>
          </PostCardBody>

          {post?.['magic-links'] &&
            post?.['magic-links'].map(link => (
              <MagicLink
                key={link.id}
                variant="post"
                magicLink={link}
              />
            ))}
        </PostCardContent>

        <PostActions
          post={post}
          deletePost={deletePost}
          setPosts={props.setPosts}
        />
      </PostCardWrapper>
    )
  );
});
PostCard.displayName = 'PostCard';

export { PostCardWrapper, PostCard, PostCardContent, PostCardHeader, PostCardName, PostCardTime, PostCardBody, PostCardText };
